import { useState, useEffect, useRef } from 'react'
import { colors, fonts } from './UI'

const MILESTONES = [
  { value: 1000,   emoji: '🌱', label: '£1,000 Reached!' },
  { value: 5000,   emoji: '⭐', label: '£5,000 Milestone!' },
  { value: 10000,  emoji: '🎯', label: '£10,000 Reached!' },
  { value: 25000,  emoji: '🚀', label: '£25,000 - Outstanding!' },
  { value: 50000,  emoji: '💎', label: '£50,000 - Incredible!' },
  { value: 100000, emoji: '👑', label: '£100,000 - You Made It!' },
]

export function checkMilestone(current, previous) {
  if (!current || !previous || current <= previous) return null
  return MILESTONES.find(m => previous < m.value && current >= m.value) || null
}

const CONFETTI_COLORS = ['#c9972b', '#e8b84b', '#f5f3ef', '#3db87a', '#4a8fe8', '#ffffff']

function Particle({ x, delay, color }) {
  return (
    <div style={{
      position: 'absolute',
      left: `${x}%`,
      top: '-10px',
      width: '8px',
      height: '8px',
      background: color,
      borderRadius: Math.random() > 0.5 ? '50%' : '0',
      animation: `confettiFall ${1.5 + Math.random()}s ${delay}s ease-in forwards`,
      transform: `rotate(${Math.random() * 360}deg)`,
      opacity: 0,
    }} />
  )
}

export default function MilestoneCelebration({ milestone, onDismiss }) {
  const [visible, setVisible] = useState(false)
  const [particles] = useState(() =>
    Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.8,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    }))
  )

  useEffect(() => {
    if (milestone) {
      setVisible(true)
      // Haptic feedback - short double pulse feels premium
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 200])
      }
      const t = setTimeout(() => { setVisible(false); setTimeout(onDismiss, 400) }, 6000)
      return () => clearTimeout(t)
    }
  }, [milestone])

  if (!milestone) return null

  return (
    <>
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(500px) rotate(720deg); opacity: 0; }
        }
        @keyframes cardIn {
          0% { transform: scale(0.8) translateY(20px); opacity: 0; }
          60% { transform: scale(1.04) translateY(-4px); }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(201,151,43,0.4); }
          50% { box-shadow: 0 0 0 12px rgba(201,151,43,0); }
        }
      `}</style>

      <div style={{
        position: 'fixed', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 300,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(6px)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.4s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }} onClick={() => { setVisible(false); setTimeout(onDismiss, 400) }}>

        {/* Confetti particles */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          {particles.map(p => <Particle key={p.id} {...p} />)}
        </div>

        <div style={{
          background: colors.navyMid,
          border: `1px solid ${colors.borderGold}`,
          borderTop: `3px solid ${colors.gold}`,
          borderRadius: '4px',
          padding: '40px 40px 32px',
          textAlign: 'center',
          maxWidth: '360px',
          width: '90vw',
          position: 'relative',
          animation: visible ? 'cardIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards' : 'none',
        }} onClick={e => e.stopPropagation()}>

          {/* Gold glow ring behind emoji */}
          <div style={{
            width: '80px', height: '80px',
            borderRadius: '50%',
            background: 'rgba(201,151,43,0.1)',
            border: `1px solid rgba(201,151,43,0.3)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            animation: 'pulse 2s infinite',
          }}>
            <span style={{ fontSize: '40px', lineHeight: 1 }}>{milestone.emoji}</span>
          </div>

          <div style={{ fontSize: '14px', color: colors.gold, letterSpacing: '0.2em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '8px' }}>
            Net Worth Milestone
          </div>

          <div style={{ fontSize: '32px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '0.04em', marginBottom: '12px' }}>
            {milestone.label}
          </div>

          <p style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, lineHeight: 1.7, marginBottom: '24px' }}>
            You hit a major milestone. Every pound saved is a step towards financial freedom. Keep going.
          </p>

          <button onClick={() => { setVisible(false); setTimeout(onDismiss, 400) }} style={{
            background: colors.gold, border: 'none', borderRadius: '2px',
            color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body,
            fontWeight: 700, padding: '12px 32px', cursor: 'pointer',
            letterSpacing: '0.1em', width: '100%',
          }}>
            Keep going 🎯
          </button>

          <div style={{ fontSize: '16px', color: colors.mutedDeep, fontFamily: fonts.body, marginTop: '12px' }}>
            Tap anywhere to dismiss
          </div>
        </div>
      </div>
    </>
  )
}
